import { useCallback, useState } from "react";
import { Box, Heading, Badge, HStack, Text } from "@chakra-ui/react";
import Card from "@/src/components/card/Card";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Get, Post } from "@/src/api";
import { useToast } from "@/src/hooks/use-toast";
import LoadingIndicator from "@/src/components/loading";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/src/components/ui/table";

import { Redirect } from "react-router-dom";

import useSuperAdmin from "@/src/hooks/useSuperAdmin";
import { useConfirmDialog } from "@/src/components/confirmDialog";

import TableBodyContent from "@/src/views/admin/onHoldPayouts/components/tableBodyContent";

export interface OnHolDpayoutsResponse {
  userId: number;
  uuid: string;
  deviceId: string;
  appId: string;
  languageCode: string;
  countryCode: string;
  sumOfPayouts: number;
  source: string;
  onHoldPayouts: {
    payoutId: number;
    valueEur: number;
    currency: string;
    coins: number;
    optionType: string;
    mail: string;
    status: string;
    fraudmlInferenceResult: {
      isFraud: boolean;
      probabilityLegit: number;
      probabilityFraud: number;
    } | null;
  }[];
  livenessReferenceImages: string[] | null;
}

const formatCurrency = (amount: number, currency: string) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

export default function OnHoldPayoutsPage() {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { toast } = useToast();
  const isSuperAdmin = useSuperAdmin();

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<OnHolDpayoutsResponse[]>({
    queryKey: ["onHoldPayouts"],
    queryFn: async () => {
      return Get("/support/payout-onhold-users");
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (userId: number) => {
      return Post(`/support/payout-onhold-users/${userId}/approve`, {});
    },
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({ queryKey: ["onHoldPayouts"] });
      toast({
        title: "Success",
        description: `Payouts for User #${userId} have been approved successfully.`,
        variant: "default",
      });
    },
    onError: (error, userId) => {
      toast({
        title: "Error",
        description: `Failed to approve payouts for User #${userId}. Please try again.`,
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (userId: number) => {
      return Post(`/support/payout-onhold-users/${userId}/reject`, {});
    },
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({ queryKey: ["onHoldPayouts"] });
      toast({
        title: "Success",
        description: `Payouts for User #${userId} have been rejected successfully.`,
        variant: "default",
      });
    },
    onError: (error, userId) => {
      toast({
        title: "Error",
        description: `Failed to reject payouts for User #${userId}. Please try again.`,
        variant: "destructive",
      });
    },
  });

  const { openDialog: openApproveDialog, ConfirmDialog: ApproveDialog } =
    useConfirmDialog({
      title: "Approve Payouts",
      description: "Are you sure you want to approve the payout(s)?",
    });

  const { openDialog: openRejectDialog, ConfirmDialog: RejectDialog } =
    useConfirmDialog({
      title: "Reject Payouts",
      description: "Are you sure you want to reject the payout(s)?",
    });

  const toggleRow = (userId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedRows(newExpanded);
  };

  const handleApprove = useCallback(
    async (userId: number) => {
      const result = await openApproveDialog();
      if (!result) return;

      approveMutation.mutate(userId);
    },
    [openApproveDialog, approveMutation],
  );

  const handleReject = useCallback(
    async (userId: number) => {
      const result = await openRejectDialog();
      if (!result) return;

      rejectMutation.mutate(userId);
    },
    [openRejectDialog, rejectMutation],
  );

  if (isLoading) return <LoadingIndicator />;

  if (!isSuperAdmin) {
    return <Redirect to={"/"} />;
  }

  return (
    <Card>
      <Box p={6}>
        <HStack justify="space-between" mb={6}>
          <Heading size="lg" color="gray.700">
            On Hold Payouts
          </Heading>
          <Badge colorScheme="orange" px={3} py={1} borderRadius="full">
            {data?.length || 0} Users
          </Badge>
        </HStack>

        <Box
          overflowX="auto"
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          border="1px"
          borderColor="gray.200"
        >
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[50px]"></TableHead>
                <TableHead className="font-semibold text-gray-700">
                  User Info
                </TableHead>
                <TableHead className="font-semibold text-gray-700">
                  Device & App
                </TableHead>
                <TableHead className="font-semibold text-gray-700">
                  Source
                </TableHead>
                <TableHead className="font-semibold text-gray-700">
                  Location
                </TableHead>
                <TableHead className="font-semibold text-gray-700">
                  Total Amount
                </TableHead>
                <TableHead className="font-semibold text-gray-700">
                  Payouts
                </TableHead>
                <TableHead className="font-semibold text-gray-700 text-center">
                  Image
                </TableHead>
                <TableHead className="font-semibold text-gray-700">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.map((user: OnHolDpayoutsResponse) => (
                <TableBodyContent
                  key={user.userId}
                  user={user}
                  toggleRow={toggleRow}
                  expandedRows={expandedRows}
                  approveMutation={approveMutation}
                  rejectMutation={rejectMutation}
                  formatCurrency={formatCurrency}
                  handleApprove={handleApprove}
                  handleReject={handleReject}
                />
              ))}
            </TableBody>
          </Table>
        </Box>

        {data?.length === 0 && (
          <Box textAlign="center" py={12}>
            <Text fontSize="lg" color="gray.500">
              No on-hold payouts found
            </Text>
          </Box>
        )}
      </Box>
      {ApproveDialog}
      {RejectDialog}
    </Card>
  );
}
